import { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Trash2, Edit, Copy, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { useModelInfo, useDeleteModel } from '@/api/hooks/useModels';
import { ApiException } from '@/api/client';

export function ModelDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const decodedId = decodeURIComponent(id);
  const navigate = useNavigate();
  const { data, isLoading, error } = useModelInfo(decodedId);
  const deleteModel = useDeleteModel();
  const [showApiKey, setShowApiKey] = useState(false);

  if (isLoading) {
    return (
      <div
        style={{
          height: 384,
          background: '#e2e8f0',
          borderRadius: 8,
          animation: 'pulse 2s infinite',
        }}
      />
    );
  }

  if (error) {
    return (
      <Card>
        <div style={{ padding: 24, color: '#991b1b', fontSize: 13 }}>
          <strong>Failed to load model:</strong>{' '}
          {error instanceof ApiException ? error.message : 'Unknown error'}
          <div style={{ marginTop: 12 }}>
            <Button variant="outline" onClick={() => navigate('/models')}>
              Back to Models
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <div style={{ padding: 24, color: '#64748b', fontSize: 13 }}>
          Model not found.{' '}
          <Link to="/models" style={{ color: '#0f172a' }}>
            Back to Models
          </Link>
        </div>
      </Card>
    );
  }

  const handleDelete = () => {
    if (confirm(`Delete model ${data.id}?`)) {
      deleteModel.mutate(data.id, {
        onSuccess: () => navigate('/models'),
      });
    }
  };

  const params = data.litellm_params ?? {};
  const info = data.model_info ?? {};
  const apiKey = typeof params.api_key === 'string' ? params.api_key : '';
  const apiBase = typeof params.api_base === 'string' ? params.api_base : '';
  const providerModel = typeof params.model === 'string' ? params.model : '';
  const maskedKey = apiKey ? `sk-...${apiKey.slice(-4)}` : '-';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>{data.id}</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <Link to={`/models/${encodeURIComponent(data.id)}/edit`}>
            <Button variant="outline">
              <Edit size={16} />
              <span>Edit</span>
            </Button>
          </Link>
          <Button variant="destructive" onClick={handleDelete}>
            <Trash2 size={16} />
            <span>Delete</span>
          </Button>
        </div>
      </div>

      <Card>
        <h2 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 12px' }}>Summary</h2>
        <dl style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 12 }}>
          <Row label="ID" value={data.id} />
          <Row label="Provider" value={data.owned_by ?? '-'} />
          <Row label="Mode" value={data.mode ?? '-'} />
          <Row
            label="Max Input Tokens"
            value={data.max_input_tokens?.toLocaleString() ?? '-'}
          />
          <Row
            label="Max Output Tokens"
            value={data.max_output_tokens?.toLocaleString() ?? '-'}
          />
        </dl>
      </Card>

      <Card>
        <h2 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 12px' }}>
          Provider Configuration
        </h2>
        <dl style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 12 }}>
          <Row
            label="Provider Model"
            value={
              <span style={{ fontFamily: 'monospace' }}>{providerModel || '-'}</span>
            }
          />
          <Row
            label="API Base"
            value={
              <span style={{ fontFamily: 'monospace' }}>
                {apiBase || '-'}
              </span>
            }
          />
          <Row
            label="API Key"
            value={
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontFamily: 'monospace' }}>
                  {apiKey ? (showApiKey ? apiKey : maskedKey) : '-'}
                </span>
                {apiKey && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowApiKey(!showApiKey)}
                    >
                      {showApiKey ? (
                        <EyeOff size={14} />
                      ) : (
                        <Eye size={14} />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigator.clipboard.writeText(apiKey)}
                    >
                      <Copy size={14} />
                    </Button>
                  </>
                )}
              </div>
            }
          />
        </dl>
      </Card>

      {(typeof info.id === 'string' ||
        typeof info.created_at === 'string' ||
        typeof info.updated_at === 'string') && (
        <Card>
          <h2 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 12px' }}>
            Internal Metadata
          </h2>
          <dl style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 12 }}>
            <Row
              label="Internal ID"
              value={
                <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                  {typeof info.id === 'string' ? info.id : '-'}
                </span>
              }
            />
            <Row
              label="Created"
              value={typeof info.created_at === 'string' ? info.created_at : '-'}
            />
            <Row
              label="Updated"
              value={typeof info.updated_at === 'string' ? info.updated_at : '-'}
            />
          </dl>
        </Card>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <dt style={{ color: '#64748b', fontSize: 13 }}>{label}</dt>
      <dd style={{ margin: 0, fontSize: 14, color: '#0f172a' }}>{value}</dd>
    </>
  );
}